/**
 * EditTenantUserRolesModal.tsx
 *
 * Okno „Firmy i role" na zakładce Użytkownicy w szczegółach tenanta (panel ITE). Dla każdej firmy,
 * do której osoba ma dostęp, osobny edytor ról (jedna lub wiele, w kolejności — pierwsza = główna;
 * ADR-0103). Każda firma ma własne role, więc „Dodaj rolę" podaje wyłącznie role TEJ firmy.
 * Okno zmienia też sam dostęp do firm: „Dodaj firmę" nadaje dostęp do kolejnej firmy tenanta (od razu
 * z rolami), „Odbierz dostęp" odbiera go razem z rolami osoby w tej firmie (do zapisu da się cofnąć).
 *
 * Zapis: PUT /api/admin/tenants/:tenantId/users/:userId/company-roles — jedno żądanie z samymi
 * różnicami (zmienione role, nadane firmy, odebrane firmy); serwer zapisuje wszystko w jednej
 * transakcji. Po sukcesie: odświeżenie listy, toast, zamknięcie.
 */

import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { Trash2, Undo2 } from 'lucide-react';

import { Badge } from '@/components/Badge';
import { Button } from '@/components/Button';
import { Dropdown } from '@/components/Dropdown';
import { Modal } from '@/components/Modal';
import { Skeleton } from '@/components/Skeleton';
import { type CompanyRoleOption, CompanyRolesEditor } from '@/components/users/CompanyRolesEditor';
import { useToast } from '@/contexts/ToastContext';
import { api } from '@/lib/api';
import { cn } from '@/lib/utils';

interface CompanyRole extends CompanyRoleOption {
  companyId: string;
}

interface RoleTemplatesResponse {
  companyRoles: CompanyRole[];
  dbUnavailable: boolean;
}

interface TenantCompany {
  id: string;
  legalName: string | null;
  shortName: string | null;
}

interface CompaniesResponse {
  companies: TenantCompany[];
  dbUnavailable?: boolean;
}

export interface EditTenantUserRolesUser {
  id: string;
  email: string;
  isSuperUser: boolean;
  companies: Array<{
    id: string;
    legalName: string | null;
    shortName: string | null;
    roles: Array<{ id: string; name: string }>;
  }>;
}

export interface EditTenantUserRolesModalProps {
  tenantId: string;
  /** Osoba, której firmy i role zmieniamy; `null` = okno zamknięte. */
  user: EditTenantUserRolesUser | null;
  onClose: () => void;
}

export function EditTenantUserRolesModal({
  tenantId,
  user,
  onClose,
}: EditTenantUserRolesModalProps) {
  // Klucz = osoba: każde otwarcie startuje ze świeżym stanem z listy (bez ręcznego resetu).
  if (!user) return null;
  return <EditRolesDialog key={user.id} tenantId={tenantId} user={user} onClose={onClose} />;
}

/** Błędy oznaczające, że migawka osoby w oknie jest nieaktualna — po nich okno się zamyka. */
const STALE_STATE_ERRORS = new Set([
  'NO_CHANGES',
  'COMPANY_ACCESS_NOT_FOUND',
  'COMPANY_ACCESS_ALREADY_GRANTED',
  'COMPANY_NOT_FOUND',
  'USER_NOT_FOUND',
]);

function sameOrder(a: readonly string[], b: readonly string[]): boolean {
  return a.length === b.length && a.every((id, i) => id === b[i]);
}

function companyLabel(c: { legalName: string | null; shortName: string | null }): string {
  return c.shortName ?? c.legalName ?? '—';
}

function EditRolesDialog({
  tenantId,
  user,
  onClose,
}: {
  tenantId: string;
  user: EditTenantUserRolesUser;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();

  const initial = useMemo(
    () => new Map(user.companies.map((c) => [c.id, c.roles.map((r) => r.id)])),
    [user],
  );
  const [draft, setDraft] = useState<Map<string, string[]>>(() => new Map(initial));
  /** Firmy dokładane w tym oknie (w kolejności dodania). */
  const [added, setAdded] = useState<string[]>([]);
  /** Firmy osoby, którym odbieramy dostęp przy zapisie. */
  const [revoked, setRevoked] = useState<Set<string>>(() => new Set());
  // Lista „Dodaj firmę" nie trzyma wyboru — po dodaniu wraca do podpowiedzi (nowy klucz = reset).
  const [addPickerKey, setAddPickerKey] = useState(0);
  const [formError, setFormError] = useState<string | null>(null);

  const rolesQuery = useQuery({
    queryKey: ['admin', 'tenants', tenantId, 'role-templates'],
    queryFn: async (): Promise<RoleTemplatesResponse> => {
      const res = await api.get<RoleTemplatesResponse>(`/admin/tenants/${tenantId}/role-templates`);
      return res.data;
    },
  });

  const companiesQuery = useQuery({
    queryKey: ['admin', 'tenants', tenantId, 'companies', 'picker'],
    queryFn: async (): Promise<CompaniesResponse> => {
      const res = await api.get<CompaniesResponse>(
        `/admin/tenants/${tenantId}/companies?limit=100`,
      );
      return res.data;
    },
  });

  const rolesByCompany = useMemo(() => {
    const map = new Map<string, CompanyRole[]>();
    for (const r of rolesQuery.data?.companyRoles ?? []) {
      const list = map.get(r.companyId) ?? [];
      list.push(r);
      map.set(r.companyId, list);
    }
    return map;
  }, [rolesQuery.data]);

  // Nazwy ról: z katalogu firmy, a gdy go jeszcze nie ma — z listy użytkowników.
  const roleNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const c of user.companies) for (const r of c.roles) map.set(r.id, r.name);
    for (const r of rolesQuery.data?.companyRoles ?? []) map.set(r.id, r.name);
    return map;
  }, [user, rolesQuery.data]);

  const tenantCompanies = useMemo(
    () => (companiesQuery.data?.companies ?? []).filter((c) => c.id),
    [companiesQuery.data],
  );

  // Wiersze okna: firmy osoby (także te do odebrania) + firmy dokładane.
  const rows = useMemo(() => {
    const own = user.companies.map((c) => ({ ...c, isNew: false }));
    const fresh = added.map((id) => {
      const c = tenantCompanies.find((tc) => tc.id === id);
      return {
        id,
        legalName: c?.legalName ?? null,
        shortName: c?.shortName ?? null,
        isNew: true,
      };
    });
    return [...own, ...fresh];
  }, [user, added, tenantCompanies]);

  const addableCompanies = tenantCompanies.filter(
    (c) => !initial.has(c.id) && !added.includes(c.id),
  );

  const changedRoles = user.companies
    .filter((c) => !revoked.has(c.id))
    .map((c) => ({ companyId: c.id, roleTemplateIds: draft.get(c.id) ?? [] }))
    .filter((c) => !sameOrder(c.roleTemplateIds, initial.get(c.companyId) ?? []));
  const grantCompanies = added.map((id) => ({
    companyId: id,
    roleTemplateIds: draft.get(id) ?? [],
  }));
  const revokeCompanyIds = user.companies.filter((c) => revoked.has(c.id)).map((c) => c.id);
  const hasChanges =
    changedRoles.length > 0 || grantCompanies.length > 0 || revokeCompanyIds.length > 0;

  // Zwykła osoba nie może zostać bez żadnej firmy (serwer pilnuje tego samego — 409).
  const remainingCount = user.companies.length - revokeCompanyIds.length + added.length;
  const leavesNoCompany = !user.isSuperUser && revokeCompanyIds.length > 0 && remainingCount === 0;

  const saveMutation = useMutation({
    mutationFn: async () => {
      // Tylko niepuste listy — firmy spoza body serwer zostawia nietknięte.
      const body: Record<string, unknown> = {};
      if (changedRoles.length > 0) body.companies = changedRoles;
      if (grantCompanies.length > 0) body.grantCompanies = grantCompanies;
      if (revokeCompanyIds.length > 0) body.revokeCompanyIds = revokeCompanyIds;
      const res = await api.put(`/admin/tenants/${tenantId}/users/${user.id}/company-roles`, body);
      return res.data;
    },
    onSuccess: () => {
      toast.show({
        variant: 'success',
        title: t('admin.tenants.detail.users.roles.toast.saved', { email: user.email }),
      });
      void queryClient.invalidateQueries({ queryKey: ['admin', 'tenants', tenantId, 'users'] });
      // Licznik użytkowników na zakładce Firmy zmienia się razem z dostępem.
      void queryClient.invalidateQueries({ queryKey: ['admin', 'tenants', tenantId, 'companies'] });
      onClose();
    },
    onError: (err) => {
      const code = isAxiosError(err)
        ? (err.response?.data?.error as string | undefined)
        : undefined;
      const msg = errorMessageForCode(code);
      toast.show({ variant: code === 'NO_CHANGES' ? 'warning' : 'error', title: msg });
      // Stan na serwerze mógł się zmienić (np. dostęp do firmy odebrany w międzyczasie).
      void queryClient.invalidateQueries({ queryKey: ['admin', 'tenants', tenantId, 'users'] });
      // Okno pracuje na migawce wiersza — przy błędach wynikających ze zmiany stanu na serwerze
      // każda kolejna próba skończyłaby się tak samo, więc zamykamy je (lista odświeża się sama).
      if (code && STALE_STATE_ERRORS.has(code)) {
        onClose();
        return;
      }
      setFormError(msg);
    },
  });

  function errorMessageForCode(code: string | undefined): string {
    switch (code) {
      case 'NO_CHANGES':
        return t('admin.tenants.detail.users.roles.errors.noChanges');
      case 'ROLE_NOT_IN_COMPANY':
        return t('admin.tenants.detail.users.roles.errors.roleNotInCompany');
      case 'COMPANY_ACCESS_NOT_FOUND':
        return t('admin.tenants.detail.users.roles.errors.companyAccessNotFound');
      case 'COMPANY_ACCESS_ALREADY_GRANTED':
        return t('admin.tenants.detail.users.roles.errors.companyAlreadyGranted');
      case 'COMPANY_NOT_FOUND':
        return t('admin.tenants.detail.users.roles.errors.companyNotFound');
      case 'LAST_COMPANY_ACCESS':
        return t('admin.tenants.detail.users.roles.lastCompanyBlocked');
      case 'USER_NOT_FOUND':
        return t('admin.tenants.detail.users.roles.errors.userNotFound');
      case 'FORBIDDEN':
        return t('admin.tenants.detail.users.roles.errors.forbidden');
      case 'TENANT_UPDATING':
        return t('admin.tenants.dbUpdatingError');
      case 'TENANT_DB_UNAVAILABLE':
        return t('admin.tenants.detail.users.roles.errors.dbUnavailable');
      default:
        return t('admin.tenants.detail.users.roles.errors.generic');
    }
  }

  function setCompanyRoles(companyId: string, next: string[]) {
    setDraft((prev) => {
      const copy = new Map(prev);
      copy.set(companyId, next);
      return copy;
    });
  }

  function addCompany(companyId: string) {
    setAdded((prev) => (prev.includes(companyId) ? prev : [...prev, companyId]));
    setCompanyRoles(companyId, []);
    setAddPickerKey((k) => k + 1);
  }

  function removeCompany(companyId: string, isNew: boolean) {
    if (isNew) {
      // Firma dodana w tym oknie — po prostu znika (nic nie trafia na serwer).
      setAdded((prev) => prev.filter((id) => id !== companyId));
      setDraft((prev) => {
        const copy = new Map(prev);
        copy.delete(companyId);
        return copy;
      });
      return;
    }
    setRevoked((prev) => new Set(prev).add(companyId));
  }

  function undoRevoke(companyId: string) {
    setRevoked((prev) => {
      const copy = new Set(prev);
      copy.delete(companyId);
      return copy;
    });
  }

  const busy = saveMutation.isPending;
  const loading = rolesQuery.isLoading || companiesQuery.isLoading;

  return (
    <Modal
      open
      onClose={onClose}
      size="lg"
      title={t('admin.tenants.detail.users.roles.modalTitle')}
      description={t('admin.tenants.detail.users.roles.modalDescription', { email: user.email })}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            {t('admin.tenants.detail.users.roles.cancel')}
          </Button>
          <Button
            variant="primary"
            onClick={() => {
              setFormError(null);
              saveMutation.mutate();
            }}
            disabled={busy || !hasChanges || leavesNoCompany}
            data-testid="edit-tenant-user-roles-submit"
          >
            {t('admin.tenants.detail.users.roles.submit')}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {formError && (
          <div
            role="alert"
            className="rounded-lg border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-900 dark:bg-red-900/20 dark:text-red-300"
          >
            {formError}
          </div>
        )}

        {user.isSuperUser && (
          <div
            role="status"
            className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-800 dark:border-amber-900 dark:bg-amber-900/20 dark:text-amber-200"
            data-testid="edit-tenant-user-roles-super-note"
          >
            {t('admin.tenants.detail.users.roles.superUserNote')}
          </div>
        )}

        {loading ? (
          <div className="flex flex-col gap-2">
            {(rows.length > 0 ? rows : [{ id: 'placeholder' }]).map((c) => (
              <Skeleton key={c.id} height={56} />
            ))}
          </div>
        ) : rolesQuery.isError || rolesQuery.data?.dbUnavailable ? (
          <div
            role="alert"
            className="rounded-lg border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-900 dark:bg-red-900/20 dark:text-red-300"
          >
            {t('admin.tenants.detail.users.roles.loadError')}
          </div>
        ) : (
          <>
            {rows.length === 0 ? (
              <p
                className="rounded-lg border border-dashed border-border-subtle px-3 py-4 text-center text-sm text-text-tertiary"
                data-testid="edit-tenant-user-roles-empty"
              >
                {t('admin.tenants.detail.users.roles.noCompanies')}
              </p>
            ) : (
              <ul className="flex flex-col divide-y divide-border-subtle rounded-lg border border-border-subtle">
                {rows.map((c) => {
                  const isRevoked = revoked.has(c.id);
                  const name = companyLabel(c);
                  const assigned = (draft.get(c.id) ?? []).map((id) => ({
                    id,
                    name: roleNameById.get(id) ?? '—',
                  }));
                  return (
                    <li
                      key={c.id}
                      className={cn('flex flex-col gap-2 px-3 py-3', isRevoked && 'bg-surface-2')}
                      data-testid={`edit-tenant-user-roles-company-${c.id}`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div
                          className={cn(
                            'text-sm font-medium text-text-primary',
                            isRevoked && 'text-text-tertiary line-through',
                          )}
                        >
                          {name}
                          {c.shortName && c.legalName && c.shortName !== c.legalName && (
                            <span className="ml-1.5 text-xs font-normal text-text-tertiary">
                              {c.legalName}
                            </span>
                          )}
                          {c.isNew && (
                            <Badge
                              tone="success"
                              size="sm"
                              className="ml-2"
                              data-testid={`edit-tenant-user-roles-new-${c.id}`}
                            >
                              {t('admin.tenants.detail.users.roles.newBadge')}
                            </Badge>
                          )}
                        </div>
                        {isRevoked ? (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => undoRevoke(c.id)}
                            disabled={busy}
                            data-testid={`edit-tenant-user-roles-undo-${c.id}`}
                          >
                            <Undo2 size={14} aria-hidden="true" />
                            {t('admin.tenants.detail.users.roles.undoRevoke')}
                          </Button>
                        ) : (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-red-600 hover:text-red-700 dark:text-red-400"
                            onClick={() => removeCompany(c.id, c.isNew)}
                            disabled={busy}
                            aria-label={t('admin.tenants.detail.users.roles.revokeAccessAria', {
                              name,
                            })}
                            data-testid={`edit-tenant-user-roles-revoke-${c.id}`}
                          >
                            <Trash2 size={14} aria-hidden="true" />
                            {t('admin.tenants.detail.users.roles.revokeAccess')}
                          </Button>
                        )}
                      </div>
                      {isRevoked ? (
                        <p className="text-xs text-text-tertiary">
                          {t('admin.tenants.detail.users.roles.revokedNote')}
                        </p>
                      ) : (
                        <CompanyRolesEditor
                          companyId={c.id}
                          assigned={assigned}
                          available={rolesByCompany.get(c.id) ?? []}
                          busy={busy}
                          onChange={(next) => setCompanyRoles(c.id, next)}
                        />
                      )}
                    </li>
                  );
                })}
              </ul>
            )}

            {companiesQuery.isError || companiesQuery.data?.dbUnavailable ? (
              <p role="alert" className="text-xs text-red-600 dark:text-red-400">
                {t('admin.tenants.detail.users.roles.companiesLoadError')}
              </p>
            ) : addableCompanies.length > 0 ? (
              <Dropdown
                key={addPickerKey}
                value={undefined}
                onChange={(companyId) => {
                  if (companyId) addCompany(companyId);
                }}
                disabled={busy}
                placeholder={`+ ${t('admin.tenants.detail.users.roles.addCompany')}`}
                className="w-64"
                aria-label={t('admin.tenants.detail.users.roles.addCompany')}
                data-testid="edit-tenant-user-roles-add-company"
                options={addableCompanies.map((c) => ({ value: c.id, label: companyLabel(c) }))}
              />
            ) : (
              <p className="text-xs text-text-tertiary" data-testid="edit-tenant-user-roles-all">
                {t('admin.tenants.detail.users.roles.noMoreCompanies')}
              </p>
            )}
          </>
        )}

        {leavesNoCompany && (
          <div
            role="alert"
            className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-800 dark:border-amber-900 dark:bg-amber-900/20 dark:text-amber-200"
            data-testid="edit-tenant-user-roles-last-company"
          >
            {t('admin.tenants.detail.users.roles.lastCompanyBlocked')}
          </div>
        )}

        <p className="text-xs text-text-tertiary">{t('admin.tenants.detail.users.roles.hint')}</p>
      </div>
    </Modal>
  );
}
